import React, { useState, useEffect, useRef, useCallback } from "react";
import { LETTER_WIDTH_PX, LETTER_HEIGHT_PX } from "../constants/pageDimensions";
import { fileToBase64 } from "../hooks/useLocalStorage";
import CollageToolbar from "./CollageToolbar";
import CollageElement from "./CollageElement";
import CropOverlay from "./CropOverlay";

const STORAGE_KEY = "striply_collage_default";
const DEBOUNCE_MS = 500;

function generateId() {
  return `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function CollageEditor() {
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState("select"); // "select" | "snip"
  const [snipTarget, setSnipTarget] = useState(null); // element id being snipped
  const [isInitialized, setIsInitialized] = useState(false);
  const saveTimeoutRef = useRef(null);
  const canvasWidth = LETTER_WIDTH_PX;
  const canvasHeight = LETTER_HEIGHT_PX;

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed.elements) && parsed.elements.length > 0) {
          setElements(parsed.elements);
        }
      }
    } catch (e) {
      console.warn("Failed to load saved collage:", e);
    }
    setIsInitialized(true);
  }, []);

  // Debounced save to localStorage
  useEffect(() => {
    if (!isInitialized) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(() => {
      try {
        const data = {
          elements,
          savedAt: Date.now(),
          version: 1,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (e) {
        console.warn("Failed to save collage:", e);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [elements, isInitialized]);

  const addElement = useCallback((url, naturalWidth, naturalHeight) => {
    // Scale to fit within canvas while maintaining aspect ratio
    const maxW = canvasWidth * 0.8;
    const maxH = canvasHeight * 0.8;
    const scale = Math.min(maxW / naturalWidth, maxH / naturalHeight, 1);
    const width = Math.round(naturalWidth * scale);
    const height = Math.round(naturalHeight * scale);
    const x = Math.round((canvasWidth - width) / 2);
    const y = Math.round((canvasHeight - height) / 2);

    const newEl = {
      id: generateId(),
      type: "image",
      url,
      x,
      y,
      width,
      height,
      cropSource: null,
    };
    setElements((prev) => [...prev, newEl]);
    setSelectedId(newEl.id);
  }, [canvasWidth, canvasHeight]);

  const handleUpload = useCallback(async (file) => {
    const base64 = await fileToBase64(file);
    const img = new Image();
    img.onload = () => {
      addElement(base64, img.naturalWidth, img.naturalHeight);
    };
    img.src = base64;
  }, [addElement]);

  const updateElement = useCallback((id, updates) => {
    setElements((prev) =>
      prev.map((el) => (el.id === id ? { ...el, ...updates } : el))
    );
  }, []);

  const deleteElement = useCallback((id) => {
    setElements((prev) => prev.filter((el) => el.id !== id));
    setSelectedId((prev) => (prev === id ? null : prev));
  }, []);

  const bringForward = useCallback((id) => {
    setElements((prev) => {
      const idx = prev.findIndex((el) => el.id === id);
      if (idx < 0 || idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  const sendBackward = useCallback((id) => {
    setElements((prev) => {
      const idx = prev.findIndex((el) => el.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx], next[idx - 1]] = [next[idx - 1], next[idx]];
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    setElements([]);
    setSelectedId(null);
    setMode("select");
    setSnipTarget(null);
  }, []);

  const toggleSnipMode = useCallback(() => {
    if (mode === "snip") {
      setMode("select");
      setSnipTarget(null);
    } else {
      setMode("snip");
      setSnipTarget(null);
    }
  }, [mode]);

  const handleElementClick = useCallback((id) => {
    if (mode === "snip") {
      setSnipTarget(id);
    } else {
      setSelectedId(id);
    }
  }, [mode]);

  const handleSnipConfirm = useCallback((elementId, sx, sy, sw, sh) => {
    const el = elements.find((e) => e.id === elementId);
    if (!el) return;

    const img = new Image();
    img.onload = () => {
      const offscreen = document.createElement("canvas");
      offscreen.width = sw;
      offscreen.height = sh;
      const ctx = offscreen.getContext("2d");
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
      const croppedUrl = offscreen.toDataURL("image/png");

      const newEl = {
        id: generateId(),
        type: "image",
        url: croppedUrl,
        x: el.x + 20,
        y: el.y + 20,
        width: Math.round(sw * (el.width / img.naturalWidth)),
        height: Math.round(sh * (el.height / img.naturalHeight)),
        cropSource: { url: el.url, sx, sy, sw, sh },
      };
      setElements((prev) => [...prev, newEl]);
      setSelectedId(newEl.id);
      setSnipTarget(null);
      setMode("select");
    };
    img.src = el.url;
  }, [elements]);

  const handleSnipCancel = useCallback(() => {
    setSnipTarget(null);
    setMode("select");
  }, []);

  const handleExportPNG = useCallback(() => {
    const offscreen = document.createElement("canvas");
    offscreen.width = canvasWidth;
    offscreen.height = canvasHeight;
    const ctx = offscreen.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw elements in z-order (index 0 = bottom)
    let loaded = 0;
    const total = elements.length;

    if (total === 0) {
      triggerDownload(offscreen);
      return;
    }

    elements.forEach((el) => {
      const img = new Image();
      img.onload = () => {
        loaded++;
        if (loaded === total) {
          // All loaded, draw in order
          elements.forEach((drawEl) => {
            const drawImg = new Image();
            drawImg.onload = () => {};
            drawImg.src = drawEl.url;
          });
          // Draw synchronously since images are cached
          elements.forEach((drawEl) => {
            const drawImg = new Image();
            drawImg.src = drawEl.url;
            ctx.drawImage(drawImg, drawEl.x, drawEl.y, drawEl.width, drawEl.height);
          });
          triggerDownload(offscreen);
        }
      };
      img.src = el.url;
    });
  }, [elements, canvasWidth, canvasHeight]);

  const triggerDownload = (canvas) => {
    const url = canvas.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = "collage.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fileInputRef = useRef(null);

  const triggerUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    if (file) {
      handleUpload(file);
    }
    if (event.target) {
      event.target.value = "";
    }
  }, [handleUpload]);

  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      handleUpload(file);
    }
  }, [handleUpload]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  // Paste support
  useEffect(() => {
    const handlePaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            handleUpload(file);
          }
          break;
        }
      }
    };
    window.addEventListener("paste", handlePaste);
    return () => window.removeEventListener("paste", handlePaste);
  }, [handleUpload]);

  const snipTargetEl = snipTarget ? elements.find((e) => e.id === snipTarget) : null;

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      <CollageToolbar
        mode={mode}
        selectedId={selectedId}
        onFileSelected={triggerUpload}
        onToggleSnip={toggleSnipMode}
        onBringForward={() => selectedId && bringForward(selectedId)}
        onSendBackward={() => selectedId && sendBackward(selectedId)}
        onDelete={() => selectedId && deleteElement(selectedId)}
        onExportPNG={handleExportPNG}
        onClearAll={clearAll}
      />
      {/* Self-healing cutting mat: the collage work surface */}
      <div
        style={{
          padding: "36px 24px 48px",
          background: [
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1px, rgba(0,0,0,0) 1px, rgba(0,0,0,0) 50px)",
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.10) 0px, rgba(255,255,255,0.10) 1px, rgba(0,0,0,0) 1px, rgba(0,0,0,0) 50px)",
            "repeating-linear-gradient(0deg, rgba(255,255,255,0.14) 0px, rgba(255,255,255,0.14) 1px, rgba(0,0,0,0) 1px, rgba(0,0,0,0) 250px)",
            "repeating-linear-gradient(90deg, rgba(255,255,255,0.14) 0px, rgba(255,255,255,0.14) 1px, rgba(0,0,0,0) 1px, rgba(0,0,0,0) 250px)",
            "linear-gradient(180deg, #35634f, #2b5743)",
          ].join(", "),
        }}
      >
      <div
        style={{
          position: "relative",
          width: canvasWidth,
          height: canvasHeight,
          minWidth: canvasWidth,
          minHeight: canvasHeight,
          border: dragOver ? "3px dashed #00a4e4" : "2px solid #1a1a1a",
          background: dragOver ? "#e8f7fd" : "#fffdf6",
          overflow: "hidden",
          margin: "0 auto",
          maxWidth: "100%",
          boxShadow: "0 10px 28px rgba(0,0,0,0.35)",
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            setSelectedId(null);
          }
        }}
      >
        {elements.length === 0 && !dragOver && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#8a8171",
            pointerEvents: "none",
            fontFamily: "'Georgia', serif",
          }}>
            <span className="material-icons" style={{ fontSize: 48, marginBottom: 12 }}>content_cut</span>
            <p style={{ fontSize: 16, margin: 0, fontStyle: "italic" }}>
              Drag &amp; drop images here, or paste from clipboard.
            </p>
            <p style={{ fontSize: 13, margin: "6px 0 0", color: "#a39a87" }}>
              Then cut them up with the snip tool and arrange your collage.
            </p>
          </div>
        )}
        {elements.map((el) => (
          <CollageElement
            key={el.id}
            element={el}
            isSelected={selectedId === el.id}
            mode={mode}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            onClick={() => handleElementClick(el.id)}
            onUpdate={(updates) => updateElement(el.id, updates)}
          />
        ))}
        {snipTargetEl && (
          <CropOverlay
            element={snipTargetEl}
            canvasWidth={canvasWidth}
            canvasHeight={canvasHeight}
            onConfirm={handleSnipConfirm}
            onCancel={handleSnipCancel}
          />
        )}
      </div>
      </div>
    </div>
  );
}

export default CollageEditor;
